import { useUnit } from 'effector-react'
import { useMutation, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast.tsx'
import { $creatorId } from '@/models/creators.ts'
import {
  addCheckInProgressId,
  removeCheckInProgressId,
} from '@/modules/tracking/funnel/models.ts'
import { handleError } from '@/utils/error'

import { funnelMonitoringService } from '../service/funnel-monitoring-service'
import type { FunnelCheck } from '../types'
import {
  type FunnelCheckCreate,
  type FunnelCheckEdit,
  FunnelStatus,
} from '../types'

const MAX_CHECKS = 15

export async function checkItemStatus(
  checkId: number,
  creatorId: number,
  refetchCount = 0,
) {
  const item = await funnelMonitoringService.getFunnelCheckDetails(
    creatorId,
    checkId,
  )

  refetchCount += 1

  if (refetchCount > MAX_CHECKS) {
    throw new Error('Max checks exceeded.')
  }

  if (item?.status === FunnelStatus.Unchecked) {
    await new Promise((resolve) => setTimeout(resolve, 2000))

    return checkItemStatus(checkId, creatorId, refetchCount)
  }

  return item
}

export const useFunnelChecksModifier = () => {
  const queryClient = useQueryClient()
  const toast = useToast()
  const creatorId = useUnit($creatorId)

  const updateItemInCache = async (item: FunnelCheck) => {
    queryClient.setQueryData<FunnelCheck[]>(
      ['tracking', 'funnel', creatorId, 'list'],
      (oldData: FunnelCheck[] | undefined) => {
        return (
          oldData?.map((oldItem) => {
            if (oldItem.id === item.id) {
              return item
            }

            return oldItem
          }) || []
        )
      },
    )
  }

  const deleteItemInCache = async (deletedId: number) => {
    queryClient.setQueryData<FunnelCheck[]>(
      ['tracking', 'funnel', creatorId, 'list'],
      (oldData: FunnelCheck[] | undefined) => {
        return oldData?.filter(({ id }) => id !== deletedId) || []
      },
    )
  }

  const deleteMutation = useMutation(
    ['tracking', 'funnel', creatorId, 'delete'],
    ({ checkId }: { checkId: number }) =>
      funnelMonitoringService.deleteFunnelCheck(creatorId!, checkId),
    {
      onSuccess(_, { checkId }) {
        deleteItemInCache(checkId)
        toast.success('Successfully removed')
      },
      onError: (error) => {
        handleError(error, 'Failed to delete funnel check')
      },
    },
  )

  const editMutation = useMutation(
    ['tracking', 'funnel', creatorId, 'edit'],
    ({ checkId, body }: { checkId: number; body: FunnelCheckEdit }) =>
      funnelMonitoringService.editFunnelCheck(creatorId!, checkId, body),
    {
      onSuccess(item) {
        updateItemInCache(item)
        toast.success('Successfully edited')
      },
      onError: (error) => {
        handleError(error, 'Failed to edit funnel check')
      },
    },
  )

  const manualCheckMutation = useMutation(
    ['tracking', 'funnel', creatorId, 'manual-check'],
    ({ checkId }: { checkId: number }) => {
      toast.info('Manual check started')

      addCheckInProgressId(checkId)

      return funnelMonitoringService.manualFunnelCheck(creatorId!, checkId)
    },
    {
      onSuccess: async (check) => {
        toast.success('Manual check triggered.')

        toast.info('Funnel check started...')
        const item = await checkItemStatus(check.id, creatorId!)

        if (item.status === FunnelStatus.Failed) {
          toast.error('Funnel check failed.', {
            description: item.failInfo || '',
          })
        } else if (item.status === FunnelStatus.Successful) {
          toast.success('Funnel check is successfull')
        }

        updateItemInCache(item)

        return item
      },
      onError: (error) => {
        handleError(error, 'Failed to manual check funnel check')
      },
      onSettled: (_, __, { checkId }) => {
        removeCheckInProgressId(checkId)
      },
    },
  )

  const addMutation = useMutation(
    ['tracking', 'funnel', creatorId, 'add'],
    ({ body }: { body: FunnelCheckCreate }) =>
      funnelMonitoringService.addFunnelCheck(creatorId!, body),
    {
      onSuccess: (funnelItem) => {
        const prevData = queryClient.getQueryData<FunnelCheck[]>([
          'tracking',
          'funnel',
          creatorId,
          'list',
        ])

        queryClient.setQueryData(
          ['tracking', 'funnel', creatorId, 'list'],
          [funnelItem, ...(prevData || [])],
        )

        toast.success('Successfully created')

        // Add check to in progress
        addCheckInProgressId(funnelItem.id)

        toast.info('Funnel check started...')
        checkItemStatus(funnelItem.id, creatorId!)
          .then((item) => {
            if (item.status === FunnelStatus.Failed) {
              toast.error('Funnel check failed.', {
                description: item.failInfo || '',
              })
            } else if (item.status === FunnelStatus.Successful) {
              toast.success('Funnel check is successfull')
            }

            updateItemInCache(item)
          })
          .catch((error) => {
            handleError(
              error,
              "We couldn't complete status check. Try again later.",
            )
          })
          .finally(() => {
            removeCheckInProgressId(funnelItem.id)
          })
      },
      onError: (error) => {
        handleError(error, 'Failed to add funnel check')
      },
    },
  )

  return {
    deleteCheck: deleteMutation,
    editCheck: editMutation,
    manualCheck: manualCheckMutation,
    addCheck: addMutation,
    updateItemInCache,
  }
}
